import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const t = {
  en: {
    pageTitle: 'Revs Signal',
    heroLine1: 'The right cars don\u2019t circulate publicly.',
    heroLine2: 'They move quietly, between the right people.',
    heroLine3: 'When something aligns, you\u2019ll hear from us.',
    formTitle: 'Define Your Signal',
    formIntro: 'Define what matters.',
    name: 'Full Name',
    email: 'Email Address',
    phone: 'Phone (optional)',
    make: 'Make',
    makePlaceholder: 'e.g. Ferrari, Porsche, Lamborghini',
    model: 'Model (optional)',
    modelPlaceholder: 'e.g. 488 GTB, 911 GT3, Aventador',
    yearFrom: 'Production Years From',
    yearTo: 'Production Years To',
    yearFromPlaceholder: 'From',
    yearToPlaceholder: 'To',
    budgetLabel: 'Budget Range ($)',
    budgetMinPlaceholder: 'Min',
    budgetMaxPlaceholder: 'Max',
    condition: 'Condition Preference',
    conditionOptions: ['', 'New / Delivery Mileage', 'Low Mileage Example', 'Well-maintained Driver', 'Concours / Collector Grade', 'Open to Options'],
    conditionPlaceholder: 'Select condition preference',
    timeline: 'Acquisition Timeline',
    timelineOptions: ['', 'Immediately (ready to purchase)', 'Within 3 months', 'Within 6 months', 'Within 12 months', 'Monitoring opportunities'],
    timelinePlaceholder: 'Select timeline...',
    assistHow: 'Preferred Approach',
    assistOptions: ['', 'Notify me when something aligns', 'Notify me and provide acquisition advisory', 'Open to discussing advisory'],
    assistPlaceholder: 'Select approach',
    notes: 'What matters beyond specifications?',
    notesPlaceholder: 'Color, provenance, history, or anything that defines the right car for you...',
    priorityAlert: 'Priority Signal',
    priorityAlertDesc: 'Receive priority access to vehicles identified through our private collector and dealer networks.',
    advisoryInterest: 'I may be interested in acquisition advisory if the right vehicle surfaces.',
    submit: 'Request Signal Access',
    phoneNote: 'A phone number allows us to reach you directly when something rare appears.',
    confidential: 'Each signal is considered. We operate within a limited number of active engagements.',
    successTitle: 'Signal Activated',
    successDesc: 'We\u2019ll be in touch when something aligns.',
    errorTitle: 'Error',
    errorDesc: 'Something went wrong. Please try again.',
    howTitle: 'How It Works',
    step1Title: 'Define',
    step1Desc: 'Share what you\u2019re looking for \u2014 marque, era, character.',
    step2Title: 'Observe',
    step2Desc: 'We watch global collector markets, private networks, and trusted circles.',
    step3Title: 'Connect',
    step3Desc: 'When something surfaces, we reach out directly with details and insight.',
    ctaTitle: 'Prefer to speak directly?',
    ctaButton: 'Contact Us',
    bottomNote: 'Each submission is reviewed. We operate within a limited number of active signals.',
    microTagline: 'This is how opportunities find you.'
  },
  es: {
    pageTitle: 'Revs Signal',
    heroLine1: 'Los coches correctos no circulan p\u00fablicamente.',
    heroLine2: 'Se mueven en silencio, entre las personas adecuadas.',
    heroLine3: 'Cuando algo se alinea, nos pondremos en contacto.',
    formTitle: 'Defina Su Se\u00f1al',
    formIntro: 'Define lo que importa.',
    name: 'Nombre Completo',
    email: 'Correo Electr\u00f3nico',
    phone: 'Tel\u00e9fono (opcional)',
    make: 'Marca',
    makePlaceholder: 'ej. Ferrari, Porsche, Lamborghini',
    model: 'Modelo (opcional)',
    modelPlaceholder: 'ej. 488 GTB, 911 GT3, Aventador',
    yearFrom: 'A\u00f1os de Producci\u00f3n Desde',
    yearTo: 'A\u00f1os de Producci\u00f3n Hasta',
    yearFromPlaceholder: 'Desde',
    yearToPlaceholder: 'Hasta',
    budgetLabel: 'Rango de Presupuesto ($)',
    budgetMinPlaceholder: 'M\u00edn',
    budgetMaxPlaceholder: 'M\u00e1x',
    condition: 'Preferencia de Condici\u00f3n',
    conditionOptions: ['', 'Nuevo / Kilometraje de Entrega', 'Ejemplo de Bajo Kilometraje', 'Conductor Bien Mantenido', 'Concours / Grado Coleccionista', 'Abierto a Opciones'],
    conditionPlaceholder: 'Seleccione preferencia de condici\u00f3n',
    timeline: 'Plazo de Adquisici\u00f3n',
    timelineOptions: ['', 'Inmediatamente (listo para comprar)', 'Dentro de 3 meses', 'Dentro de 6 meses', 'Dentro de 12 meses', 'Monitoreando oportunidades'],
    timelinePlaceholder: 'Seleccione plazo...',
    assistHow: 'Enfoque Preferido',
    assistOptions: ['', 'Notif\u00edqueme cuando algo se alinee', 'Notif\u00edqueme y brinde asesor\u00eda de adquisici\u00f3n', 'Abierto a discutir asesor\u00eda'],
    assistPlaceholder: 'Seleccione enfoque',
    notes: '\u00bfQu\u00e9 importa m\u00e1s all\u00e1 de las especificaciones?',
    notesPlaceholder: 'Color, procedencia, historia, o lo que define el coche ideal para usted...',
    priorityAlert: 'Se\u00f1al Prioritaria',
    priorityAlertDesc: 'Reciba acceso prioritario a veh\u00edculos identificados a trav\u00e9s de nuestras redes privadas.',
    advisoryInterest: 'Podr\u00eda estar interesado en asesor\u00eda de adquisici\u00f3n si aparece el veh\u00edculo adecuado.',
    submit: 'Solicitar Acceso al Se\u00f1al',
    phoneNote: 'Un n\u00famero de tel\u00e9fono nos permite contactarle directamente cuando algo raro aparece.',
    confidential: 'Cada se\u00f1al es considerada. Operamos con un n\u00famero limitado de compromisos activos.',
    successTitle: 'Se\u00f1al Activada',
    successDesc: 'Nos pondremos en contacto cuando algo se alinee.',
    errorTitle: 'Error',
    errorDesc: 'Algo sali\u00f3 mal. Por favor intente de nuevo.',
    howTitle: 'C\u00f3mo Funciona',
    step1Title: 'Definir',
    step1Desc: 'Comparta lo que busca \u2014 marca, era, car\u00e1cter.',
    step2Title: 'Observar',
    step2Desc: 'Vigilamos mercados globales, redes privadas y c\u00edrculos de confianza.',
    step3Title: 'Conectar',
    step3Desc: 'Cuando algo emerge, le contactamos directamente con detalles y perspectiva.',
    ctaTitle: '\u00bfPrefiere hablar directamente?',
    ctaButton: 'Cont\u00e1ctenos',
    bottomNote: 'Cada solicitud es revisada. Operamos con un n\u00famero limitado de se\u00f1ales activas.',
    microTagline: 'As\u00ed es como las oportunidades te encuentran.'
  },
  pt: {
    pageTitle: 'Revs Signal',
    heroLine1: 'Os carros certos n\u00e3o circulam publicamente.',
    heroLine2: 'Eles se movem silenciosamente, entre as pessoas certas.',
    heroLine3: 'Quando algo se alinha, voc\u00ea ouvir\u00e1 de n\u00f3s.',
    formTitle: 'Defina Seu Sinal',
    formIntro: 'Defina o que importa.',
    name: 'Nome Completo',
    email: 'Endere\u00e7o de E-mail',
    phone: 'Telefone (opcional)',
    make: 'Marca',
    makePlaceholder: 'ex. Ferrari, Porsche, Lamborghini',
    model: 'Modelo (opcional)',
    modelPlaceholder: 'ex. 488 GTB, 911 GT3, Aventador',
    yearFrom: 'Anos de Produ\u00e7\u00e3o De',
    yearTo: 'Anos de Produ\u00e7\u00e3o At\u00e9',
    yearFromPlaceholder: 'De',
    yearToPlaceholder: 'At\u00e9',
    budgetLabel: 'Faixa de Or\u00e7amento ($)',
    budgetMinPlaceholder: 'M\u00edn',
    budgetMaxPlaceholder: 'M\u00e1x',
    condition: 'Prefer\u00eancia de Condi\u00e7\u00e3o',
    conditionOptions: ['', 'Novo / Quilometragem de Entrega', 'Exemplo de Baixa Quilometragem', 'Condutor Bem Mantido', 'Concours / Grau Colecionador', 'Aberto a Op\u00e7\u00f5es'],
    conditionPlaceholder: 'Selecione prefer\u00eancia de condi\u00e7\u00e3o',
    timeline: 'Prazo de Aquisi\u00e7\u00e3o',
    timelineOptions: ['', 'Imediatamente (pronto para comprar)', 'Dentro de 3 meses', 'Dentro de 6 meses', 'Dentro de 12 meses', 'Monitorando oportunidades'],
    timelinePlaceholder: 'Selecione prazo...',
    assistHow: 'Abordagem Preferida',
    assistOptions: ['', 'Notifique-me quando algo se alinhar', 'Notifique-me e forne\u00e7a consultoria de aquisi\u00e7\u00e3o', 'Aberto a discutir consultoria'],
    assistPlaceholder: 'Selecione abordagem',
    notes: 'O que importa al\u00e9m das especifica\u00e7\u00f5es?',
    notesPlaceholder: 'Cor, proced\u00eancia, hist\u00f3ria, ou o que define o carro certo para voc\u00ea...',
    priorityAlert: 'Sinal Priorit\u00e1rio',
    priorityAlertDesc: 'Receba acesso priorit\u00e1rio a ve\u00edculos identificados atrav\u00e9s de nossas redes privadas.',
    advisoryInterest: 'Posso estar interessado em consultoria de aquisi\u00e7\u00e3o se o ve\u00edculo certo surgir.',
    submit: 'Solicitar Acesso ao Sinal',
    phoneNote: 'Um n\u00famero de telefone nos permite contact\u00e1-lo diretamente quando algo raro aparece.',
    confidential: 'Cada sinal \u00e9 considerado. Operamos com um n\u00famero limitado de compromissos ativos.',
    successTitle: 'Sinal Ativado',
    successDesc: 'Entraremos em contato quando algo se alinhar.',
    errorTitle: 'Erro',
    errorDesc: 'Algo deu errado. Por favor tente novamente.',
    howTitle: 'Como Funciona',
    step1Title: 'Definir',
    step1Desc: 'Compartilhe o que procura \u2014 marca, era, car\u00e1ter.',
    step2Title: 'Observar',
    step2Desc: 'Vigiamos mercados globais, redes privadas e c\u00edrculos de confian\u00e7a.',
    step3Title: 'Conectar',
    step3Desc: 'Quando algo emerge, entramos em contato diretamente com detalhes e perspectiva.',
    ctaTitle: 'Prefere falar diretamente?',
    ctaButton: 'Contate-nos',
    bottomNote: 'Cada solicita\u00e7\u00e3o \u00e9 revisada. Operamos com um n\u00famero limitado de sinais ativos.',
    microTagline: '\u00c9 assim que as oportunidades encontram voc\u00ea.'
  },
  fr: {
    pageTitle: 'Revs Signal',
    heroLine1: 'Les bonnes voitures ne circulent pas publiquement.',
    heroLine2: 'Elles se d\u00e9placent discr\u00e8tement, entre les bonnes personnes.',
    heroLine3: 'Quand quelque chose s\u2019aligne, vous en serez inform\u00e9.',
    formTitle: 'D\u00e9finissez Votre Signal',
    formIntro: 'D\u00e9finissez ce qui compte.',
    name: 'Nom Complet',
    email: 'Adresse E-mail',
    phone: 'T\u00e9l\u00e9phone (optionnel)',
    make: 'Marque',
    makePlaceholder: 'ex. Ferrari, Porsche, Lamborghini',
    model: 'Mod\u00e8le (optionnel)',
    modelPlaceholder: 'ex. 488 GTB, 911 GT3, Aventador',
    yearFrom: 'Ann\u00e9es de Production De',
    yearTo: 'Ann\u00e9es de Production \u00c0',
    yearFromPlaceholder: 'De',
    yearToPlaceholder: '\u00c0',
    budgetLabel: 'Fourchette de Budget ($)',
    budgetMinPlaceholder: 'Min',
    budgetMaxPlaceholder: 'Max',
    condition: 'Pr\u00e9f\u00e9rence de Condition',
    conditionOptions: ['', 'Neuf / Kilom\u00e9trage de Livraison', 'Exemple Faible Kilom\u00e9trage', 'Conducteur Bien Entretenu', 'Concours / Grade Collectionneur', 'Ouvert aux Options'],
    conditionPlaceholder: 'S\u00e9lectionnez pr\u00e9f\u00e9rence de condition',
    timeline: 'D\u00e9lai d\'Acquisition',
    timelineOptions: ['', 'Imm\u00e9diatement (pr\u00eat \u00e0 acheter)', 'Dans les 3 mois', 'Dans les 6 mois', 'Dans les 12 mois', 'Surveillance des opportunit\u00e9s'],
    timelinePlaceholder: 'S\u00e9lectionnez un d\u00e9lai...',
    assistHow: 'Approche Pr\u00e9f\u00e9r\u00e9e',
    assistOptions: ['', 'Me notifier quand quelque chose s\u2019aligne', 'Me notifier et fournir un conseil d\u2019acquisition', 'Ouvert \u00e0 discuter de conseil'],
    assistPlaceholder: 'S\u00e9lectionnez approche',
    notes: 'Qu\u2019est-ce qui compte au-del\u00e0 des sp\u00e9cifications?',
    notesPlaceholder: 'Couleur, provenance, histoire, ou ce qui d\u00e9finit la bonne voiture pour vous...',
    priorityAlert: 'Signal Prioritaire',
    priorityAlertDesc: 'Recevez un acc\u00e8s prioritaire aux v\u00e9hicules identifi\u00e9s via nos r\u00e9seaux priv\u00e9s.',
    advisoryInterest: 'Je pourrais \u00eatre int\u00e9ress\u00e9 par un conseil d\u2019acquisition si le bon v\u00e9hicule se pr\u00e9sente.',
    submit: 'Demander l\u2019Acc\u00e8s au Signal',
    phoneNote: 'Un num\u00e9ro de t\u00e9l\u00e9phone nous permet de vous contacter directement quand quelque chose de rare appara\u00eet.',
    confidential: 'Chaque signal est consid\u00e9r\u00e9. Nous op\u00e9rons avec un nombre limit\u00e9 d\u2019engagements actifs.',
    successTitle: 'Signal Activ\u00e9',
    successDesc: 'Nous vous contacterons quand quelque chose s\u2019alignera.',
    errorTitle: 'Erreur',
    errorDesc: 'Une erreur est survenue. Veuillez r\u00e9essayer.',
    howTitle: 'Comment \u00c7a Fonctionne',
    step1Title: 'D\u00e9finir',
    step1Desc: 'Partagez ce que vous cherchez \u2014 marque, \u00e9poque, caract\u00e8re.',
    step2Title: 'Observer',
    step2Desc: 'Nous observons les march\u00e9s mondiaux, les r\u00e9seaux priv\u00e9s et les cercles de confiance.',
    step3Title: 'Connecter',
    step3Desc: 'Quand quelque chose \u00e9merge, nous vous contactons directement avec d\u00e9tails et perspective.',
    ctaTitle: 'Pr\u00e9f\u00e9rez-vous parler directement?',
    ctaButton: 'Contactez-nous',
    bottomNote: 'Chaque soumission est examin\u00e9e. Nous op\u00e9rons avec un nombre limit\u00e9 de signaux actifs.',
    microTagline: 'C\u2019est ainsi que les opportunit\u00e9s vous trouvent.'
  },
  it: {
    pageTitle: 'Revs Signal',
    heroLine1: 'Le auto giuste non circolano pubblicamente.',
    heroLine2: 'Si muovono in silenzio, tra le persone giuste.',
    heroLine3: 'Quando qualcosa si allinea, ne sarai informato.',
    formTitle: 'Definisci Il Tuo Segnale',
    formIntro: 'Definisci ci\u00f2 che conta.',
    name: 'Nome Completo',
    email: 'Indirizzo E-mail',
    phone: 'Telefono (opzionale)',
    make: 'Marca',
    makePlaceholder: 'es. Ferrari, Porsche, Lamborghini',
    model: 'Modello (opzionale)',
    modelPlaceholder: 'es. 488 GTB, 911 GT3, Aventador',
    yearFrom: 'Anni di Produzione Da',
    yearTo: 'Anni di Produzione A',
    yearFromPlaceholder: 'Da',
    yearToPlaceholder: 'A',
    budgetLabel: 'Fascia di Budget ($)',
    budgetMinPlaceholder: 'Min',
    budgetMaxPlaceholder: 'Max',
    condition: 'Preferenza Condizione',
    conditionOptions: ['', 'Nuovo / Chilometraggio di Consegna', 'Esempio Basso Chilometraggio', 'Conducente Ben Mantenuto', 'Concours / Grado Collezionista', 'Aperto alle Opzioni'],
    conditionPlaceholder: 'Seleziona preferenza condizione',
    timeline: 'Tempistica di Acquisizione',
    timelineOptions: ['', 'Immediatamente (pronto all\'acquisto)', 'Entro 3 mesi', 'Entro 6 mesi', 'Entro 12 mesi', 'Monitoraggio opportunit\u00e0'],
    timelinePlaceholder: 'Seleziona tempistica...',
    assistHow: 'Approccio Preferito',
    assistOptions: ['', 'Avvisami quando qualcosa si allinea', 'Avvisami e fornisci consulenza di acquisizione', 'Aperto a discutere consulenza'],
    assistPlaceholder: 'Seleziona approccio',
    notes: 'Cosa conta oltre alle specifiche?',
    notesPlaceholder: 'Colore, provenienza, storia, o ci\u00f2 che definisce l\u2019auto giusta per te...',
    priorityAlert: 'Segnale Prioritario',
    priorityAlertDesc: 'Ricevi accesso prioritario ai veicoli identificati attraverso le nostre reti private.',
    advisoryInterest: 'Potrei essere interessato a consulenza di acquisizione se il veicolo giusto emerge.',
    submit: 'Richiedi Accesso al Segnale',
    phoneNote: 'Un numero di telefono ci consente di contattarti direttamente quando qualcosa di raro appare.',
    confidential: 'Ogni segnale viene considerato. Operiamo con un numero limitato di impegni attivi.',
    successTitle: 'Segnale Attivato',
    successDesc: 'Ti contatteremo quando qualcosa si allineer\u00e0.',
    errorTitle: 'Errore',
    errorDesc: 'Qualcosa \u00e8 andato storto. Per favore riprova.',
    howTitle: 'Come Funziona',
    step1Title: 'Definire',
    step1Desc: 'Condividi ci\u00f2 che cerchi \u2014 marchio, epoca, carattere.',
    step2Title: 'Osservare',
    step2Desc: 'Osserviamo i mercati globali, le reti private e i circoli di fiducia.',
    step3Title: 'Connettere',
    step3Desc: 'Quando qualcosa emerge, ti contattiamo direttamente con dettagli e prospettiva.',
    ctaTitle: 'Preferisci parlare direttamente?',
    ctaButton: 'Contattaci',
    bottomNote: 'Ogni richiesta viene esaminata. Operiamo con un numero limitato di segnali attivi.',
    microTagline: '\u00c8 cos\u00ec che le opportunit\u00e0 ti trovano.'
  }
};

const CarAlert = () => {
  useDocumentMeta({
    title: 'Car Alert | Revs Vibes',
    description: 'Register for Revs Vibes car alerts to stay informed about collector vehicles matching your criteria.',
    canonical: 'https://www.revsvibes.com/car-alert',
    image: 'https://customer-assets.emergentagent.com/job_luxury-motors-13/artifacts/quzfrp4g_FB_IMG_1770695066393.jpg',
  });
  const { language } = useLanguage();
  const l = t[language] || t.en;
  const [submitting, setSubmitting] = useState(false);
  const [formState, setFormState] = useState('idle'); // idle | success | error
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    name: '', email: '', phone: '', make: '', model: '',
    year_from: '', year_to: '', budget_min: '', budget_max: '',
    condition: '', timeline: '', assist_type: '', priority_alert: false, advisory_interest: false, notes: ''
  });

  const update = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
    if (errors[field]) setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  };
  const toggle = (field) => () => setForm({ ...form, [field]: !form[field] });

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = true;
    if (!form.email.trim()) errs.email = true;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = true;
    if (!form.make.trim()) errs.make = true;
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSubmitting(true);
    setErrors({});
    try {
      const payload = {
        ...form,
        year_from: form.year_from ? parseInt(form.year_from) : null,
        year_to: form.year_to ? parseInt(form.year_to) : null,
        budget_min: form.budget_min ? parseInt(form.budget_min) : null,
        budget_max: form.budget_max ? parseInt(form.budget_max) : null,
      };
      const res = await fetch(`${API_URL}/api/car-alerts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error();
      setFormState('success');
      setForm({ name: '', email: '', phone: '', make: '', model: '', year_from: '', year_to: '', budget_min: '', budget_max: '', condition: '', timeline: '', assist_type: '', priority_alert: false, advisory_interest: false, notes: '' });
    } catch {
      setFormState('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="signal-hero relative bg-black text-white overflow-hidden h-[420px] sm:h-[500px] md:h-[600px] clickable-card">
        <div className="absolute inset-0">
          <picture>
            <source
              media="(max-width: 767px) and (orientation: portrait)"
              srcSet="https://customer-assets.emergentagent.com/job_f184c80b-9543-4a62-923b-8fe110e27c5d/artifacts/cafv03bv_f8e29b2b-8dac-4597-badf-a03f2a9555e6.png"
            />
            <img
              src="https://customer-assets.emergentagent.com/job_9636621a-d57c-404d-b012-24f5c61ec43a/artifacts/cfzvhnl8_wp4171960.jpg"
              alt="Revs Signal"
              className="w-full h-full object-cover opacity-30 signal-hero-img"
            />
          </picture>
        </div>
        <div className="relative h-full flex items-center justify-center">
          <div className="text-center px-4 max-w-2xl">
            <h1 data-testid="car-alert-title" className="text-4xl md:text-5xl font-semibold mb-10">{l.pageTitle}</h1>
            <div className="space-y-3">
              <p className="text-base sm:text-lg text-gray-300 font-light leading-relaxed">{l.heroLine1}</p>
              <p className="text-base sm:text-lg text-gray-300 font-light leading-relaxed">{l.heroLine2}</p>
              <p className="text-base sm:text-lg text-white/90 font-light leading-relaxed">{l.heroLine3}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Micro-tagline */}
      <div className="py-14 sm:py-16 bg-white text-center">
        <p className="text-[11px] sm:text-xs tracking-[0.24em] font-light" style={{ color: '#a8adb5' }}>
          {l.microTagline}
        </p>
      </div>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-2xl font-semibold text-center mb-14">{l.howTitle}</h2>
          <div className="space-y-10">
            {[
              { num: '01', title: l.step1Title, desc: l.step1Desc },
              { num: '02', title: l.step2Title, desc: l.step2Desc },
              { num: '03', title: l.step3Title, desc: l.step3Desc }
            ].map((step) => (
              <div key={step.num} className="flex gap-6">
                <span className="text-sm font-light flex-shrink-0 w-6 leading-[1.75] tracking-[0.06em]" style={{ color: 'rgba(0,0,0,0.30)' }}>{step.num}</span>
                <div>
                  <h3 className="font-medium text-base mb-1 text-black">{step.title}</h3>
                  <p className="text-gray-400 leading-relaxed text-[14px]">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-20 bg-white">
        <div className="max-w-xl mx-auto px-6">
          {formState === 'success' ? (
            <div className="text-center py-16" data-testid="car-alert-success">
              <div className="w-16 h-16 rounded-full bg-black/5 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="text-black" size={24} />
              </div>
              <h2 className="text-2xl font-semibold mb-3">{l.successTitle}</h2>
              <p className="text-gray-500 mb-8 max-w-sm mx-auto">{l.successDesc}</p>
              <button
                onClick={() => setFormState('idle')}
                className="text-[13px] text-gray-400 hover:text-gray-700 transition-colors duration-300"
                data-testid="car-alert-send-another"
              >
                {language === 'en' ? 'Submit another signal' : language === 'es' ? 'Enviar otra señal' : language === 'pt' ? 'Enviar outro sinal' : language === 'fr' ? 'Soumettre un autre signal' : 'Invia un altro segnale'} &rarr;
              </button>
            </div>
          ) : (
          <>
          <div className="mb-10">
            <h2 className="text-2xl font-semibold mb-3" data-testid="signal-form-title">{l.formTitle}</h2>
            <p className="text-gray-400 text-[15px] font-light">{l.formIntro}</p>
          </div>

          {formState === 'error' && (
            <div className="bg-red-50 border border-red-200 rounded-sm px-4 py-3 mb-6 text-sm text-red-700" data-testid="car-alert-error-banner">
              {l.errorDesc}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6" noValidate>
            {/* Name & Email */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm text-gray-500 mb-1.5">{l.name} *</label>
                <Input value={form.name} onChange={update('name')} data-testid="car-alert-name" className={`border-gray-200 focus:ring-1 focus:ring-gray-400 ${errors.name ? 'border-red-400' : ''}`} />
                {errors.name && <p className="text-xs text-red-500 mt-1" data-testid="car-alert-name-error">{language === 'en' ? 'Required' : language === 'es' ? 'Requerido' : language === 'pt' ? 'Obrigatório' : language === 'fr' ? 'Requis' : 'Obbligatorio'}</p>}
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1.5">{l.email} *</label>
                <Input type="email" value={form.email} onChange={update('email')} data-testid="car-alert-email" className={`border-gray-200 focus:ring-1 focus:ring-gray-400 ${errors.email ? 'border-red-400' : ''}`} />
                {errors.email && <p className="text-xs text-red-500 mt-1" data-testid="car-alert-email-error">{language === 'en' ? 'Valid email required' : language === 'es' ? 'Correo válido requerido' : language === 'pt' ? 'Email válido obrigatório' : language === 'fr' ? 'Email valide requis' : 'Email valida richiesta'}</p>}
              </div>
            </div>

            {/* Phone & Make */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm text-gray-500 mb-1.5">{l.phone}</label>
                <Input value={form.phone} onChange={update('phone')} data-testid="car-alert-phone" className="border-gray-200 focus:ring-1 focus:ring-gray-400" />
                <p className="text-xs text-gray-300 mt-1.5">{l.phoneNote}</p>
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1.5">{l.make} *</label>
                <Input value={form.make} onChange={update('make')} placeholder={l.makePlaceholder} data-testid="car-alert-make" className={`border-gray-200 focus:ring-1 focus:ring-gray-400 ${errors.make ? 'border-red-400' : ''}`} />
                {errors.make && <p className="text-xs text-red-500 mt-1" data-testid="car-alert-make-error">{language === 'en' ? 'Required' : language === 'es' ? 'Requerido' : language === 'pt' ? 'Obrigatório' : language === 'fr' ? 'Requis' : 'Obbligatorio'}</p>}
              </div>
            </div>

            {/* Model */}
            <div>
              <label className="block text-sm text-gray-500 mb-1.5">{l.model}</label>
              <Input value={form.model} onChange={update('model')} placeholder={l.modelPlaceholder} data-testid="car-alert-model" className="border-gray-200 focus:ring-1 focus:ring-gray-400" />
            </div>

            {/* Budget Range */}
            <div>
              <label className="block text-sm text-gray-500 mb-1.5">{l.budgetLabel}</label>
              <div className="grid grid-cols-2 gap-5">
                <Input type="number" min="0" value={form.budget_min} onChange={update('budget_min')} placeholder={l.budgetMinPlaceholder} data-testid="car-alert-budget-min" className="border-gray-200 focus:ring-1 focus:ring-gray-400" />
                <Input type="number" min="0" value={form.budget_max} onChange={update('budget_max')} placeholder={l.budgetMaxPlaceholder} data-testid="car-alert-budget-max" className="border-gray-200 focus:ring-1 focus:ring-gray-400" />
              </div>
            </div>

            {/* Year Range */}
            <div className="grid grid-cols-2 gap-5">
              <div>
                <label className="block text-sm text-gray-500 mb-1.5">{l.yearFrom}</label>
                <select
                  value={form.year_from}
                  onChange={update('year_from')}
                  className="w-full h-10 px-3 rounded-md border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-gray-400"
                  data-testid="car-alert-year-from"
                >
                  <option value="">{l.yearFromPlaceholder}</option>
                  {Array.from({ length: 2027 - 1900 + 1 }, (_, i) => 2027 - i).map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1.5">{l.yearTo}</label>
                <select
                  value={form.year_to}
                  onChange={update('year_to')}
                  className="w-full h-10 px-3 rounded-md border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-gray-400"
                  data-testid="car-alert-year-to"
                >
                  <option value="">{l.yearToPlaceholder}</option>
                  {Array.from({ length: 2027 - 1900 + 1 }, (_, i) => 2027 - i).map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Condition */}
            <div>
              <label className="block text-sm text-gray-500 mb-1.5">{l.condition}</label>
              <select
                value={form.condition}
                onChange={update('condition')}
                className="w-full h-10 px-3 rounded-md border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-gray-400"
                data-testid="car-alert-condition"
              >
                {l.conditionOptions.map((opt, i) => (
                  <option key={i} value={opt}>{opt || l.conditionPlaceholder}</option>
                ))}
              </select>
            </div>

            {/* Timeline */}
            <div>
              <label className="block text-sm text-gray-500 mb-1.5">{l.timeline}</label>
              <select
                value={form.timeline}
                onChange={update('timeline')}
                className="w-full h-10 px-3 rounded-md border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-gray-400"
                data-testid="car-alert-timeline"
              >
                {l.timelineOptions.map((opt, i) => (
                  <option key={i} value={opt}>{opt || l.timelinePlaceholder}</option>
                ))}
              </select>
            </div>

            {/* How should we engage */}
            <div>
              <label className="block text-sm text-gray-500 mb-1.5">{l.assistHow}</label>
              <select
                value={form.assist_type}
                onChange={update('assist_type')}
                className="w-full h-10 px-3 rounded-md border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-gray-400"
                data-testid="car-alert-assist"
              >
                {l.assistOptions.map((opt, i) => (
                  <option key={i} value={opt}>{opt || l.assistPlaceholder}</option>
                ))}
              </select>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm text-gray-500 mb-1.5">{l.notes}</label>
              <Textarea value={form.notes} onChange={update('notes')} placeholder={l.notesPlaceholder} rows={4} data-testid="car-alert-notes" className="border-gray-200 focus:ring-1 focus:ring-gray-400" />
            </div>

            {/* Priority Options */}
            <div className="space-y-4 pt-4">
              <label
                className="flex items-start gap-3 cursor-pointer"
                data-testid="car-alert-priority-label"
              >
                <input
                  type="checkbox"
                  checked={form.priority_alert}
                  onChange={toggle('priority_alert')}
                  className="mt-1 h-4 w-4 rounded border-gray-300 accent-black"
                  data-testid="car-alert-priority"
                />
                <div>
                  <span className="text-sm text-gray-700">{l.priorityAlert}</span>
                  <p className="text-xs text-gray-400 mt-0.5">{l.priorityAlertDesc}</p>
                </div>
              </label>

              <label
                className="flex items-start gap-3 cursor-pointer"
                data-testid="car-alert-advisory-label"
              >
                <input
                  type="checkbox"
                  checked={form.advisory_interest}
                  onChange={toggle('advisory_interest')}
                  className="mt-1 h-4 w-4 rounded border-gray-300 accent-black"
                  data-testid="car-alert-advisory"
                />
                <div>
                  <span className="text-sm text-gray-500">{l.advisoryInterest}</span>
                </div>
              </label>
            </div>

            <div className="pt-4">
              <Button type="submit" size="lg" className="w-full bg-black text-white hover:bg-gray-800 text-[13px] tracking-[0.08em] py-5 rounded-sm" disabled={submitting} data-testid="car-alert-submit">
                {submitting && <Loader2 className="animate-spin mr-2" size={18} />}
                {l.submit}
              </Button>
            </div>

            <p className="text-xs text-gray-300 text-center pt-1">{l.confidential}</p>
          </form>

          {/* Bottom note */}
          <div className="mt-14 pt-10 border-t border-gray-100">
            <p className="text-[11px] sm:text-xs tracking-[0.24em] font-light text-center" style={{ color: '#a8adb5' }}>
              {l.bottomNote}
            </p>
          </div>
          </>
          )}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 bg-white text-center">
        <p className="text-gray-400 text-base mb-6 font-light">{l.ctaTitle}</p>
        <Link to="/contact">
          <Button variant="outline" className="px-10 py-4 text-[13px] tracking-[0.08em] rounded-sm border-gray-300 hover:border-gray-500 transition-colors duration-300" data-testid="car-alert-contact-btn">
            {l.ctaButton}
          </Button>
        </Link>
      </section>
    </div>
  );
};

export default CarAlert;
